import { Plus, Trash2 } from 'lucide-react';
import {
  ALL_PRODUCTS,
  DURATION_TYPES,
  type DurationType,
  type LineInput,
  type ProductOption,
} from '@/api/affiliate';

/** Sentinel meaning "not chosen yet" — duration_type must always be an explicit choice. */
export const DURATION_TYPE_UNSET = '' as unknown as DurationType;

/** True if any line still has an unset duration_type. */
export function hasUnsetDuration(lines: LineInput[]): boolean {
  return lines.some((l) => !l.duration_type);
}

/**
 * Editable table of per-product commission lines. Each line targets a product
 * (or "(all products)" = product_code NULL) with its own %, min active subs and
 * duration. Used by both the agreement and template forms.
 */
export default function LineEditor({
  lines,
  products,
  onChange,
}: {
  lines: LineInput[];
  products: ProductOption[];
  onChange: (lines: LineInput[]) => void;
}) {
  const productValue = (l: LineInput) =>
    l.product_code == null ? ALL_PRODUCTS : `${l.system_code}::${l.product_code}`;

  const update = (idx: number, patch: Partial<LineInput>) => {
    onChange(lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };

  const setProduct = (idx: number, value: string) => {
    if (value === ALL_PRODUCTS) {
      update(idx, { system_code: 'jumpstart', product_code: null });
      return;
    }
    const [system_code, code] = value.split('::');
    update(idx, { system_code, product_code: code });
  };

  const addLine = () => {
    onChange([
      ...lines,
      {
        system_code: 'jumpstart',
        product_code: null,
        commission_pct: 10,
        min_active_subs: 0,
        duration_type: DURATION_TYPE_UNSET,
        duration_months: null,
      },
    ]);
  };

  const removeLine = (idx: number) => onChange(lines.filter((_, i) => i !== idx));

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-ink-secondary">Per-product commission lines</span>
        <button
          type="button"
          onClick={addLine}
          className="inline-flex items-center gap-1 text-xs text-gold hover:text-gold-light"
        >
          <Plus size={14} /> Add line
        </button>
      </div>

      {lines.length === 0 ? (
        <p className="text-xs text-ink-muted">
          No per-product lines — the header default (all products) rate applies.
        </p>
      ) : (
        <div className="space-y-2">
          {lines.map((line, idx) => (
            <div
              key={idx}
              className="grid grid-cols-12 gap-2 items-end rounded-lg p-2 bg-navy-deeper border border-navy-border"
            >
              <div className="col-span-12 sm:col-span-4">
                <label className="block text-[11px] text-ink-muted mb-1">Product</label>
                <select
                  value={productValue(line)}
                  onChange={(e) => setProduct(idx, e.target.value)}
                  className="w-full h-9 px-2 rounded border border-navy-border bg-navy-card text-ink-primary text-sm"
                >
                  <option value={ALL_PRODUCTS}>(all products) — default</option>
                  {products.map((p) => (
                    <option key={`${p.system_code}::${p.code}`} value={`${p.system_code}::${p.code}`}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-span-4 sm:col-span-2">
                <label className="block text-[11px] text-ink-muted mb-1">%</label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={line.commission_pct}
                  onChange={(e) => update(idx, { commission_pct: Number(e.target.value) })}
                  className="w-full h-9 px-2 rounded border border-navy-border bg-navy-card text-ink-primary text-sm num"
                />
              </div>

              <div className="col-span-4 sm:col-span-2">
                <label className="block text-[11px] text-ink-muted mb-1">Min subs</label>
                <input
                  type="number"
                  min={0}
                  value={line.min_active_subs}
                  onChange={(e) => update(idx, { min_active_subs: Number(e.target.value) })}
                  className="w-full h-9 px-2 rounded border border-navy-border bg-navy-card text-ink-primary text-sm num"
                />
              </div>

              <div className="col-span-3 sm:col-span-2">
                <label className="block text-[11px] text-ink-muted mb-1">Duration</label>
                <select
                  value={line.duration_type || ''}
                  onChange={(e) => {
                    const dt = e.target.value as DurationType;
                    update(idx, {
                      duration_type: dt,
                      duration_months: dt === 'fixed' ? line.duration_months ?? 12 : null,
                    });
                  }}
                  className={`w-full h-9 px-2 rounded border bg-navy-card text-ink-primary text-sm ${
                    line.duration_type ? 'border-navy-border' : 'border-danger'
                  }`}
                >
                  <option value="" disabled>
                    — choose —
                  </option>
                  {DURATION_TYPES.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-span-6 sm:col-span-1">
                {line.duration_type === 'fixed' && (
                  <>
                    <label className="block text-[11px] text-ink-muted mb-1">Mo.</label>
                    <input
                      type="number"
                      min={1}
                      value={line.duration_months ?? 12}
                      onChange={(e) => update(idx, { duration_months: Number(e.target.value) })}
                      className="w-full h-9 px-2 rounded border border-navy-border bg-navy-card text-ink-primary text-sm num"
                    />
                  </>
                )}
              </div>

              <div className="col-span-3 sm:col-span-1 flex justify-end">
                <button
                  type="button"
                  onClick={() => removeLine(idx)}
                  className="h-9 w-9 inline-flex items-center justify-center rounded text-ink-muted hover:text-danger hover:bg-navy-hover"
                  title="Remove line"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
